/**
 * Feeds service API calls.
 *
 * This module provides functions for RSS feed subscription management
 * including creating, listing, deleting, and retrying feeds.
 */

import apiClient from './api';

/**
 * Get all feed subscriptions for the current user
 * @returns {Promise<Object>} Feed list response with feeds array and total count
 */
export const getFeeds = async () => {
  try {
    const response = await apiClient.get('/api/feeds/');
    return response.data;
  } catch (error) {
    throw new Error(
      error.response?.data?.detail || 'Failed to fetch feeds'
    );
  }
};

/**
 * Create feed subscription(s) from URL or OPML
 * @param {string} inputText - RSS feed URL or OPML content
 * @returns {Promise<Object>} Feed response (single) or import response (OPML)
 */
export const createFeed = async (inputText) => {
  try {
    const response = await apiClient.post('/api/feeds/', { input_text: inputText });
    return response.data;
  } catch (error) {
    throw new Error(
      error.response?.data?.detail || 'Failed to add feed'
    );
  }
};

/**
 * Delete a feed subscription
 * @param {number} feedId - Feed ID to delete
 * @returns {Promise<void>}
 */
export const deleteFeed = async (feedId) => {
  try {
    await apiClient.delete(`/api/feeds/${feedId}`);
  } catch (error) {
    throw new Error(
      error.response?.data?.detail || 'Failed to delete feed'
    );
  }
};

/**
 * Retry a feed that has errors
 * @param {number} feedId - Feed ID to retry
 * @returns {Promise<Object>} Updated feed response
 */
export const retryFeed = async (feedId) => {
  try {
    const response = await apiClient.post(`/api/feeds/${feedId}/retry`);
    return response.data;
  } catch (error) {
    throw new Error(
      error.response?.data?.detail || 'Failed to retry feed'
    );
  }
};

export const getSourceSubscriptions = async () => {
  try {
    const response = await apiClient.get('/api/sources/');
    return response.data;
  } catch (error) {
    throw new Error(
      error.response?.data?.detail || 'Failed to fetch source subscriptions'
    );
  }
};

export const createSourceSubscription = async ({ source_type, name, config }) => {
  try {
    const response = await apiClient.post('/api/sources/', { source_type, name, config });
    return response.data;
  } catch (error) {
    throw new Error(
      error.response?.data?.detail || 'Failed to create source subscription'
    );
  }
};

export const deleteSourceSubscription = async (id) => {
  try {
    await apiClient.delete(`/api/sources/${id}`);
  } catch (error) {
    throw new Error(
      error.response?.data?.detail || 'Failed to delete source subscription'
    );
  }
};

/**
 * Get article sources (domains) with counts and quality scores
 * @param {Object} params - Query parameters
 * @param {number} params.page - Page number (default: 1)
 * @param {number} params.per_page - Items per page (default: 50)
 * @param {string} params.sort_by - Sort order: article_count, score, unread_count
 * @returns {Promise<Object>} Sources list response
 */
export const getArticleSources = async (params = {}) => {
  try {
    const queryParams = new URLSearchParams({
      page: params.page || 1,
      per_page: params.per_page || 50,
    });
    if (params.sort_by) {
      queryParams.set('sort_by', params.sort_by);
    }
    const response = await apiClient.get(`/api/articles/sources?${queryParams}`);
    return response.data;
  } catch (error) {
    throw new Error(
      error.response?.data?.detail || 'Failed to fetch sources'
    );
  }
};
